/**
 * Вкладка «Управление» карточки поля (v3 IA, эталон founder 2026-07-11).
 *
 * Собирает расформированные вкладки «Атрибуты» + «Передачи»:
 *   1. Паспорт поля — название (правка) + тип угодья (смена → журнал) +
 *      признак «рисовая система» (открывает АМП в форме посева).
 *   2. Оборот и передача — жизненный цикл (вывод из оборота / архив, причина
 *      обязательна) + история передач (FieldTransfersTab).
 *
 * Логика lifecycle/land-category — прежняя вкладка «Атрибуты» (модель ADR-049,
 * API field_card.py не менялся).
 */
import { useState } from 'react';
import { Loader2, Info, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import type { FieldDetail } from '@/components/fields/FieldOverviewTab';
import { FieldTransfersTab } from '@/pages/structure/components/FieldTransfersTab';
import type { FarmOption } from '@/pages/structure/components/TransferFieldDialog';
import {
  useSetLandCategory,
  useSetRiceSystem,
  useTransitionLifecycle,
} from './api';
import {
  LIFECYCLE_STEPS,
  lifecycleLabel,
  type LifecycleStatus,
} from './constants';
import {
  useLandCategories,
  makeCodeLabeler,
} from '@/lib/reference/fieldCatalogs';

interface Props {
  detail: FieldDetail;
  fieldId: string;
  /**
   * Имя и «Рисовая система» (`PATCH /fields/{id}`) — `PERM.fieldContentWrite`
   * (AK-929): гл. агроном хозяйства их правит, хотя `canWrite` у него ложь.
   * Не передан → `canWrite`: право на структуру всегда влечёт право на
   * содержимое (бэк: tenant-writer и `farm_admin` проходят оба гейта), так
   * что по умолчанию кнопка не шире ручки.
   */
  canEditContent?: boolean;
  canWrite: boolean;
  /** Передать в другое хозяйство — `PERM.fieldTransfer` (уже `canWrite` у админа хозяйства). */
  canTransfer: boolean;
  farms: FarmOption[];
  onRename: () => void;
  onChanged: () => void;
}

export function FieldManagementTab({
  detail,
  fieldId,
  canEditContent: canEditContentProp,
  canWrite,
  canTransfer,
  farms,
  onRename,
  onChanged,
}: Props) {
  const canEditContent = canEditContentProp ?? canWrite;
  const currentLifecycle: LifecycleStatus = detail.is_archived
    ? 'archived'
    : ((detail.lifecycle_status as LifecycleStatus) ?? 'active');

  const [landCode, setLandCode] = useState<string>(detail.land_category_code ?? '');
  const [nextStatus, setNextStatus] = useState<LifecycleStatus>(currentLifecycle);
  const [reason, setReason] = useState<string>('');

  // Типы угодий — из reference.land_category (GET /reference/land-categories),
  // а не из копии списка в коде.
  const landCatalog = useLandCategories();
  const landCategories = landCatalog.data ?? [];
  const landCategoryLabel = makeCodeLabeler(landCatalog.data);

  const setLand = useSetLandCategory(fieldId);
  const setRice = useSetRiceSystem(fieldId);
  const transition = useTransitionLifecycle(fieldId);

  function handleError(err: unknown, fallback: string) {
    if (err instanceof ApiError) toast.error(err.detail);
    else toast.error(fallback);
  }

  async function saveLandCategory() {
    if (!landCode) {
      toast.error('Выберите тип угодья');
      return;
    }
    try {
      await setLand.mutateAsync({ land_category_code: landCode });
      toast.success('Тип угодья сохранён');
      onChanged();
    } catch (err) {
      handleError(err, 'Не удалось сменить тип угодья');
    }
  }

  async function toggleRiceSystem(next: boolean) {
    try {
      await setRice.mutateAsync(next);
      toast.success(
        next
          ? 'Поле отмечено как рисовая система'
          : 'Признак рисовой системы снят',
      );
      onChanged();
    } catch (err) {
      handleError(err, 'Не удалось сохранить признак рисовой системы');
    }
  }

  async function saveLifecycle() {
    if (!reason.trim()) {
      toast.error('Причина перехода обязательна');
      return;
    }
    try {
      await transition.mutateAsync({ status: nextStatus, reason: reason.trim() });
      toast.success(`Состояние: ${lifecycleLabel(nextStatus)}`);
      setReason('');
      onChanged();
    } catch (err) {
      handleError(err, 'Не удалось сменить состояние');
    }
  }

  const lifecycleChanged = nextStatus !== currentLifecycle;
  const area = detail.area_ha_fact ?? detail.area_ha_accounting;

  return (
    <div className="space-y-3 py-3">
      {/* ── Паспорт поля ── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Паспорт поля</CardTitle>
          <p className="text-sm text-muted-foreground">
            Правки сохраняются в журнал — что, когда, кто.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Название */}
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">Название</div>
              <div className="text-sm font-medium truncate">{detail.name}</div>
            </div>
            {canEditContent && (
              <Button variant="outline" size="sm" onClick={onRename}>
                <Pencil className="h-4 w-4 mr-2" />
                Изменить
              </Button>
            )}
          </div>

          {/* Площадь (read-only, по эталонному контуру) */}
          <div>
            <div className="text-xs text-muted-foreground">
              Площадь (по эталонному контуру)
            </div>
            <div className="text-sm font-medium">
              {area != null
                ? `${area.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} га`
                : '—'}
            </div>
          </div>

          {/* Тип угодья */}
          <div className="max-w-sm space-y-2">
            <Label>Тип угодья</Label>
            {/* Справочник грузится — скелетон вместо пустого списка. */}
            {landCatalog.isLoading ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <Select
                value={landCode}
                onValueChange={setLandCode}
                disabled={!canWrite || landCategories.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="— выбрать —" />
                </SelectTrigger>
                <SelectContent>
                  {landCategories.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <p className="text-xs text-muted-foreground">
              Влияет на доступные посевы (напр. под пастбище нет посева-уборки).
              Текущий:{' '}
              {landCategoryLabel(detail.land_category_code) ??
                (detail.land_category_code ? '…' : 'не задан')}
              .
            </p>
            {!landCatalog.isLoading && landCategories.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Справочник угодий сейчас недоступен — остальные разделы вкладки
                работают.
              </p>
            )}
          </div>
          {canWrite && (
            <Button
              size="sm"
              onClick={saveLandCategory}
              disabled={setLand.isPending || landCode === (detail.land_category_code ?? '')}
            >
              {setLand.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Сохранить тип угодья
            </Button>
          )}

          {/* Рисовая система — открывает АМП в форме посева */}
          <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
            <div className="min-w-0 space-y-1">
              <Label htmlFor="rice-system-toggle">Рисовая система</Label>
              <p className="text-xs text-muted-foreground">
                Поле рисовой оросительной системы. Тогда при выборе «Оставить
                под пар» добавляется АМП — агромелиоративное поле.
              </p>
            </div>
            <Switch
              id="rice-system-toggle"
              checked={detail.is_rice_system === true}
              onCheckedChange={(v) => void toggleRiceSystem(v)}
              disabled={!canEditContent || setRice.isPending}
              aria-label="Рисовая система"
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Оборот и передача ── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Оборот и передача</CardTitle>
          <p className="text-sm text-muted-foreground">
            Жизненный цикл поля в обороте. Переход требует причины — она уходит в
            журнал.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Степпер текущего состояния */}
          <ol className="flex flex-wrap items-center gap-1" aria-label="Жизненный цикл">
            {LIFECYCLE_STEPS.map((step, i) => {
              const active = step.code === currentLifecycle;
              return (
                <li key={step.code} className="flex items-center gap-1">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs',
                      active
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {step.label}
                  </span>
                  {i < LIFECYCLE_STEPS.length - 1 && (
                    <span aria-hidden className="h-px w-4 bg-border" />
                  )}
                </li>
              );
            })}
          </ol>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Новое состояние</Label>
              <Select
                value={nextStatus}
                onValueChange={(v) => setNextStatus(v as LifecycleStatus)}
                disabled={!canWrite}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LIFECYCLE_STEPS.map((s) => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                Причина <span className="text-muted-foreground">(обязательно)</span>
              </Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Кратко — что произошло"
                disabled={!canWrite}
              />
            </div>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              «Выведено» — поле остаётся в системе, но исчезает из планирования.
              «Архив» скрывает поле насовсем (историю сохраняем, можно вернуть).
            </AlertDescription>
          </Alert>

          {canWrite && (
            <Button
              size="sm"
              onClick={saveLifecycle}
              disabled={transition.isPending || !lifecycleChanged || !reason.trim()}
            >
              {transition.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Сменить состояние
            </Button>
          )}
        </CardContent>
      </Card>

      {/* ── Передачи (история + передать в другое хозяйство) ── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Передачи</CardTitle>
          <p className="text-sm text-muted-foreground">
            Передача поля в другое хозяйство или отделение и история передач.
          </p>
        </CardHeader>
        <CardContent>
          <FieldTransfersTab
            fieldId={fieldId}
            fieldName={detail.name}
            currentFarmId={detail.farm_id}
            currentFarmName={detail.farm_name}
            areaHa={detail.area_ha_fact ?? detail.area_ha_accounting}
            farms={farms}
            canManage={!detail.is_archived && canTransfer}
          />
        </CardContent>
      </Card>
    </div>
  );
}
